import {
  CATCH_BOARD_DEFAULT_OWNER_FILTER,
  CATCH_BOARD_DEFAULT_READINESS_FILTER,
  CATCH_BOARD_OWNER_FILTERS,
  CATCH_BOARD_READINESS_FILTERS,
} from 'src/screens/catch-board/catchBoardConstants'

/**
 * Sticky Catch Board filters (owner · readiness · hide closed).
 * Search is never persisted — it stays ephemeral.
 *
 * Storage key is per account so shared browsers don’t mix queues.
 */
export const CATCH_BOARD_FILTER_PREFS_KEY_PREFIX = 'catchBoard.filterPrefs'

const getStorage = () => {
  if (typeof window === 'undefined' || !window.localStorage) {
    return null
  }
  return window.localStorage
}

/**
 * @param {string|null|undefined} accountId
 * @returns {string|null}
 */
export function buildCatchBoardFilterPrefsStorageKey(accountId) {
  const id = String(accountId || '').trim()
  if (!id) {
    return null
  }
  return `${CATCH_BOARD_FILTER_PREFS_KEY_PREFIX}.${id}`
}

function isValidOwnerFilter(value) {
  return CATCH_BOARD_OWNER_FILTERS.includes(value)
}

function isValidReadinessFilter(value) {
  return CATCH_BOARD_READINESS_FILTERS.includes(value)
}

/**
 * Normalize sticky fields only (drops search and unknown keys).
 * Invalid enums fall back to board defaults.
 * @param {object} [raw]
 * @returns {{ ownerFilter: string, readinessFilter: string, hideClosed: boolean }}
 */
export function normalizeCatchBoardFilterPrefs(raw = {}) {
  const ownerFilter = isValidOwnerFilter(raw?.ownerFilter)
    ? raw.ownerFilter
    : CATCH_BOARD_DEFAULT_OWNER_FILTER
  const readinessFilter = isValidReadinessFilter(raw?.readinessFilter)
    ? raw.readinessFilter
    : CATCH_BOARD_DEFAULT_READINESS_FILTER
  const hideClosed = Boolean(raw?.hideClosed)

  return {
    ownerFilter,
    readinessFilter,
    hideClosed,
  }
}

/**
 * Load sticky filters for an account. Null when missing / unreadable / no account.
 * Does not include search.
 * @param {string|null|undefined} accountId
 * @param {Storage|null} [storage]
 * @returns {{ ownerFilter: string, readinessFilter: string, hideClosed: boolean }|null}
 */
export function loadCatchBoardFilterPrefs(
  accountId,
  storage = getStorage(),
) {
  const key = buildCatchBoardFilterPrefsStorageKey(accountId)
  if (!key || !storage) {
    return null
  }

  try {
    const raw = storage.getItem(key)
    if (!raw) {
      return null
    }
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null
    }
    return normalizeCatchBoardFilterPrefs(parsed)
  } catch {
    return null
  }
}

/**
 * Persist sticky filters for an account. Ignores search.
 * @param {string|null|undefined} accountId
 * @param {{ ownerFilter?: string, readinessFilter?: string, hideClosed?: boolean, search?: string }} filters
 * @param {Storage|null} [storage]
 * @returns {boolean} true when written
 */
export function saveCatchBoardFilterPrefs(
  accountId,
  filters = {},
  storage = getStorage(),
) {
  const key = buildCatchBoardFilterPrefsStorageKey(accountId)
  if (!key || !storage) {
    return false
  }

  const prefs = normalizeCatchBoardFilterPrefs(filters)

  try {
    storage.setItem(key, JSON.stringify(prefs))
    return true
  } catch {
    // Quota / private mode — prefs are best-effort.
    return false
  }
}

/**
 * Merge sticky prefs onto board filter defaults. Search always starts empty.
 * @param {{ ownerFilter?: string, readinessFilter?: string, hideClosed?: boolean, search?: string }} [defaults]
 * @param {{ ownerFilter?: string, readinessFilter?: string, hideClosed?: boolean }|null} [prefs]
 */
export function mergeCatchBoardFilterPrefs(
  defaults = {},
  prefs = null,
) {
  const base = {
    ownerFilter: defaults.ownerFilter || CATCH_BOARD_DEFAULT_OWNER_FILTER,
    readinessFilter:
      defaults.readinessFilter || CATCH_BOARD_DEFAULT_READINESS_FILTER,
    hideClosed: Boolean(defaults.hideClosed),
    search: '',
  }

  if (!prefs) {
    return {
      ...base,
      search: String(defaults.search || ''),
    }
  }

  const sticky = normalizeCatchBoardFilterPrefs(prefs)
  return {
    ...base,
    ...sticky,
    search: '',
  }
}
